/**
 * Trip Comments Component
 * 
 * Displays and manages trip-level comments
 */

import { useState, useEffect } from 'react';
import { View, ScrollView, Pressable, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { ThemedText, SubHeading, Caption, Body } from '@/components/ui/Text';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { subscribeToTripComments, createComment, updateComment, deleteComment, type Comment } from '@/services/comments';
// NOTE: Comment notifications to collaborators require Cloud Functions with FCM
// Local notifications would incorrectly notify the commenter about their own comment
// Deferred post-launch: send collaborator notifications via Cloud Function + FCM.
// Tracking: docs/PRELAUNCH_DEFERRED_ITEMS.md#notifications-cross-device-fcm
import { useUserStore } from '@/state/userStore';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import type { Trip } from '@/services/trips';

type TripCommentsProps = {
  trip: Trip;
  currentUserId: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** When false, skip Firestore work (e.g. Wallet tab blurred). */
  subscriptionEnabled?: boolean;
};

export function TripComments({
  trip,
  currentUserId,
  expanded,
  onExpandedChange,
  subscriptionEnabled = true,
}: TripCommentsProps) {
  const theme = useTheme();
  const profile = useUserStore((state) => state.profile);
  const { confirm: confirmAction } = useConfirmDialog();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [savingEditCommentId, setSavingEditCommentId] = useState<string | null>(null);
  const isFocused = useIsFocused();

  // Subscribe to comments. Focus-gated so the Firestore listener tears down when
  // the enclosing tab/screen is blurred — otherwise the native listener keeps
  // firing callbacks into the JS thread and can trigger permission-denied noise
  // during background transitions.
  useEffect(() => {
    if (!trip.id) return;
    if (!subscriptionEnabled || !isFocused) return;

    const unsubscribe = subscribeToTripComments(trip.id, (tripComments) => {
      setComments(tripComments);
    });

    return () => {
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors
        }
      }
    };
  }, [trip.id, isFocused, subscriptionEnabled]);

  const handleSubmitComment = async () => {
    if (!newCommentText.trim() || !trip.id || !currentUserId) return;

    setIsSubmitting(true);
    try {
      const userName = profile?.name || 'Unknown';
      const commentText = newCommentText.trim();
      
      await createComment(trip.id, currentUserId, userName, commentText);
      setNewCommentText('');

      // NOTE: Push notifications to collaborators require Cloud Functions with FCM
      // Local notifications would notify the commenter about their own comment
      // The comment will appear for collaborators via real-time Firestore subscription
    } catch (error: any) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[TripComments] Error creating comment', { message });
      const errorMessage = error?.message || 'Failed to add comment. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingText(comment.text);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editingText.trim() || !trip.id) return;

    setSavingEditCommentId(commentId);
    try {
      await updateComment(trip.id, commentId, editingText.trim());
      setEditingCommentId(null);
      setEditingText('');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[TripComments] Error updating comment', { message });
      Alert.alert('Error', 'Failed to update comment. Please try again.');
    } finally {
      setSavingEditCommentId(null);
    }
  };

  const handleDeleteComment = (comment: Comment) => {
    confirmAction({
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await deleteComment(trip.id, comment.id);
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          console.error('[TripComments] Error deleting comment', { message });
          Alert.alert('Error', 'Failed to delete comment. Please try again.');
        }
      },
    });
  };

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <CollapsibleSection
      title="Comments"
      icon="chatbubbles-outline"
      defaultExpanded={true}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    >
      <View style={{ gap: theme.spacing(2) }}>
        {/* Comment Input */}
        <View style={{ gap: theme.spacing(0.75) }}>
          <InputField
            placeholder="Add a comment..."
            value={newCommentText}
            onChangeText={setNewCommentText}
            multiline
            numberOfLines={3}
            style={{ minHeight: 80 }}
          />
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
            Keep everyone aligned with notes, links, or reminders.
          </Caption>
          <Button
            label="Post comment"
            onPress={handleSubmitComment}
            disabled={!newCommentText.trim() || isSubmitting}
            icon={<Ionicons name="send-outline" size={18} color={theme.colors.surface} />}
          />
        </View>

        {/* Comments List */}
        {comments.length === 0 ? (
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(3), gap: theme.spacing(1) }}>
            <Ionicons name="chatbubbles-outline" size={28} color={theme.colors.textSecondary} />
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              No comments yet. Share plans or receipts notes here.
            </Caption>
          </View>
        ) : (
          <View style={{ gap: theme.spacing(2) }}>
            {comments.map((comment) => {
              const isOwnComment = comment.userId === currentUserId;
              const isEditing = editingCommentId === comment.id;
              const isSavingEdit = savingEditCommentId === comment.id;

              return (
                <Card key={comment.id} variant="outlined">
                  <View style={{ gap: theme.spacing(1) }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <View style={{ flex: 1 }}>
                        <ThemedText variant="bodyStrong">{comment.userName}</ThemedText>
                        <Caption color={theme.colors.textSecondary}>
                          {formatTimeAgo(comment.createdAt)}
                          {comment.updatedAt.getTime() !== comment.createdAt.getTime() && ' (edited)'}
                        </Caption>
                      </View>
                      {isOwnComment && !isEditing && (
                        <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                          <Pressable
                            onPress={() => handleEditComment(comment)}
                            style={({ pressed }) => ({
                              padding: theme.spacing(0.5),
                              opacity: pressed ? 0.7 : 1,
                            })}
                          >
                            <Ionicons name="pencil-outline" size={16} color={theme.colors.textSecondary} />
                          </Pressable>
                          <Pressable
                            onPress={() => handleDeleteComment(comment)}
                            style={({ pressed }) => ({
                              padding: theme.spacing(0.5),
                              opacity: pressed ? 0.7 : 1,
                            })}
                          >
                            <Ionicons name="trash-outline" size={16} color={theme.colors.error} />
                          </Pressable>
                        </View>
                      )}
                    </View>
                    {isEditing ? (
                      <View style={{ gap: theme.spacing(1) }}>
                        <InputField
                          value={editingText}
                          onChangeText={setEditingText}
                          multiline
                          numberOfLines={3}
                          style={{ minHeight: 60 }}
                        />
                        <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                          <Button
                            label="Save"
                            onPress={() => handleSaveEdit(comment.id)}
                            disabled={!editingText.trim() || isSavingEdit}
                            loading={isSavingEdit}
                            variant="secondary"
                            style={{ flex: 1 }}
                          />
                          <Button
                            label="Cancel"
                            onPress={() => {
                              setEditingCommentId(null);
                              setEditingText('');
                            }}
                            variant="ghost"
                            style={{ flex: 1 }}
                            disabled={isSavingEdit}
                          />
                        </View>
                      </View>
                    ) : (
                      <Body>{comment.text}</Body>
                    )}
                  </View>
                </Card>
              );
            })}
          </View>
        )}
      </View>
    </CollapsibleSection>
  );
}

